import React, { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";

import DashboardLayout from "../../components/DashboardLayout";
import WeaveLoader from "../../components/common/WeaveLoader";
import { toolsService } from "../../services/tools";
import { getApiErrorMessage } from "../../utils/axios";
import DomainFinder from "./DomainFinder";
import EmdOpportunities from "./EmdOpportunities";

const FALLBACK_TOOLS = [
  {
    id: "domain_finder",
    name: "Domain Finder",
    description: "I know my niche — find available domains that fit it.",
    href: "/tools/domain-finder/",
  },
  {
    id: "emd_opportunities",
    name: "EMD Opportunities",
    description: "Find search queries whose exact-match .com is still available.",
    href: "/tools/emd/",
  },
];

const toolHref = (id) => {
  if (id === "domain_finder") return "/tools/domain-finder/";
  if (id === "emd_opportunities") return "/tools/emd/";
  return `/tools/${id}/`;
};

const ToolsHome = () => {
  const [tools, setTools] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    toolsService.listTools(
      (res) => {
        const list = res.data?.tools || [];
        setTools(
          list.length
            ? list.map((t) => ({ ...t, href: toolHref(t.id) }))
            : FALLBACK_TOOLS,
        );
      },
      (err) => {
        setError(getApiErrorMessage(err, "Could not load tools"));
        setTools(FALLBACK_TOOLS);
      },
    );
  }, []);

  if (!tools) {
    return (
      <div className="flex justify-center py-16">
        <WeaveLoader size={48} />
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-theme-text opacity-60">
        Utilities to help you pick a niche and domain before you build.
      </p>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {tools.map((tool) => (
          <Link
            key={tool.id}
            to={tool.href}
            className="rounded-xl border border-theme-secondary p-5 transition hover:border-theme-primary hover:bg-black hover:bg-opacity-5"
          >
            <h2 className="text-lg font-semibold text-theme-text">{tool.name}</h2>
            <p className="mt-2 text-sm text-theme-text opacity-60">{tool.description}</p>
            <span className="mt-4 inline-block text-sm text-theme-primary">Open →</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const ToolsIndex = (authProps) => {
  const location = useLocation();
  const features = authProps.currentUser?.features || {};
  if (!features.tools) {
    return <Navigate to="/" replace />;
  }

  const isHome = location.pathname === "/tools/" || location.pathname === "/tools";
  const navConfig = [
    { id: "all", label: "All tools", to: "/tools/", active: isHome },
    {
      id: "domain",
      label: "Domain Finder",
      to: "/tools/domain-finder/",
      active: location.pathname.includes("/domain-finder"),
    },
    {
      id: "emd",
      label: "EMD",
      to: "/tools/emd/",
      active: location.pathname.includes("/emd"),
    },
  ];

  return (
    <DashboardLayout title="Tools" currentUser={authProps.currentUser} navConfig={navConfig}>
      <Routes>
        <Route path="" element={<ToolsHome />} />
        <Route path="domain-finder/" element={<DomainFinder currentUser={authProps.currentUser} />} />
        <Route path="domain-finder/:runHash/" element={<DomainFinder currentUser={authProps.currentUser} />} />
        <Route path="emd/" element={<EmdOpportunities currentUser={authProps.currentUser} />} />
        <Route path="emd/:runHash/" element={<EmdOpportunities currentUser={authProps.currentUser} />} />
        <Route path="*" element={<Navigate to="/tools/" replace />} />
      </Routes>
    </DashboardLayout>
  );
};

export default ToolsIndex;
